import React, { use, useCallback, useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { LinksContext } from '../components/liveloading';
import { LinkModel } from '../models/link';
import { AppContext } from '../utils/context';
import { useInputFieldSetter } from '../utils/hooks';
import { logError, sortByDate } from '../utils/misc';

const LinkView: React.FC<{
    readonly link: LinkModel;
    readonly onDelete: (link: LinkModel | undefined) => void;
}> = ({ link, onDelete }) => {
    const handleDelete = useCallback(() => {
        onDelete(link);
    }, [link, onDelete]);

    return (
        <tr>
            <td>
                <a href={link.url} rel="noreferrer" target="_blank">
                    {link.url}
                </a>
            </td>
            <td>
                <a href={link.target} rel="noreferrer" target="_blank">
                    {link.target}
                </a>
            </td>
            <td>
                <Button onClick={handleDelete} variant="danger">
                    Delete
                </Button>
            </td>
        </tr>
    );
};

export const LinksPage: React.FC = () => {
    const { apiAccessor } = use(AppContext);
    const { refresh, set, models } = use(LinksContext);
    const [loading, setLoading] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState<LinkModel | undefined>(undefined);
    const [showCreateLink, setShowCreateLink] = useState(false);
    const [newLinkUrl, setCreateLinkUrlCB, setCreateLinkUrl] = useInputFieldSetter('https://');

    const handleDeleteLink = useCallback(() => {
        if (!linkToDelete) {
            return;
        }

        void (async () => {
            try {
                await toast.promise(linkToDelete.delete(apiAccessor), {
                    success: `Deleted link "${linkToDelete.url}"!`,
                    pending: `Deleting link "${linkToDelete.url}"...`,
                    error: {
                        render({ data }) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                            const error = data as Error;
                            return `Error deleting link: ${error.message}`;
                        },
                    },
                });

                const modelsCopy = new Map(models);
                modelsCopy.delete(linkToDelete.id);
                set(modelsCopy);
            } catch (error: unknown) {
                logError(error);
            } finally {
                setLinkToDelete(undefined);
            }
        })();
    }, [linkToDelete, models, set, apiAccessor]);

    const handleCreateLink = useCallback(() => {
        void (async () => {
            try {
                const link = await toast.promise(LinkModel.create(newLinkUrl, apiAccessor), {
                    success: `Created link "${newLinkUrl}"!`,
                    pending: `Creating link "${newLinkUrl}"...`,
                    error: {
                        render({ data }) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                            const error = data as Error;
                            return `Error creating link "${newLinkUrl}": ${error.message}`;
                        },
                    },
                });

                const modelsCopy = new Map(models);
                modelsCopy.set(link.id, link);
                set(modelsCopy);
            } catch (error: unknown) {
                logError(error);
            } finally {
                setShowCreateLink(false);
            }
        })();
    }, [newLinkUrl, models, set, apiAccessor]);

    const showCreateLinkDialog = useCallback(() => {
        setCreateLinkUrl('https://');
        setShowCreateLink(true);
    }, [setCreateLinkUrl]);

    const hideCreateLinkDialog = useCallback(() => {
        setShowCreateLink(false);
    }, []);

    const hideDeleteLinkDialog = useCallback(() => {
        setLinkToDelete(undefined);
    }, []);

    useEffect(() => {
        if (loading || models) {
            return;
        }

        // eslint-disable-next-line react-hooks/set-state-in-effect, @eslint-react/set-state-in-effect
        setLoading(true);
        void (async () => {
            try {
                await refresh();
                setLoading(false);
            } catch (error: unknown) {
                logError(error);
            }
        })();
    }, [refresh, loading, models]);

    const refreshButton = useCallback(() => {
        void (async () => {
            try {
                await toast.promise(refresh(), {
                    success: 'Links refreshed!',
                    pending: 'Refreshing links...',
                    error: {
                        render({ data }) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                            const error = data as Error;
                            return `Error refreshing links: ${error.message}`;
                        },
                    },
                });
            } catch (error: unknown) {
                logError(error);
            }
        })();
    }, [refresh]);

    if (loading || !models) {
        return (
            <>
                <h1>Manage links</h1>
                <br />
                <h3>Loading...</h3>
            </>
        );
    }

    const linkRows = Array.from(models.values())
        // eslint-disable-next-line unicorn/no-array-sort
        .sort(sortByDate)
        .map((link) => <LinkView key={link.id} link={link} onDelete={setLinkToDelete} />);

    return (
        <>
            <Modal onHide={hideDeleteLinkDialog} show={!!linkToDelete}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete file?</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>Are you sure to delete the link "{linkToDelete?.url}"?</p>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={hideDeleteLinkDialog} variant="secondary">
                        No
                    </Button>
                    <Button onClick={handleDeleteLink} variant="primary">
                        Yes
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal onHide={hideCreateLinkDialog} show={showCreateLink}>
                <Modal.Header closeButton>
                    <Modal.Title>Create link</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p>
                        Please enter the target of the link:
                        <Form.Control name="createLink" onChange={setCreateLinkUrlCB} type="text" value={newLinkUrl} />
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={hideCreateLinkDialog} variant="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateLink} variant="primary">
                        Create
                    </Button>
                </Modal.Footer>
            </Modal>
            <h1>
                Manage links
                <span className="p-3" />
                <Button onClick={showCreateLinkDialog} variant="primary">
                    Create new link
                </Button>
                <span className="p-3" />
                <Button onClick={refreshButton} variant="secondary">
                    Refresh
                </Button>
            </h1>
            <br />
            <Table bordered striped>
                <thead>
                    <tr>
                        <th>Link</th>
                        <th>Target</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>{linkRows}</tbody>
            </Table>
        </>
    );
};
